import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Lock } from 'lucide-react';
import clsx from 'clsx';
import {
  categories,
  getCategory,
  getToolsByCategory,
  type ToolDef,
} from '../registry';

/**
 * `/tools/:category` — grid of tool cards filtered by the URL's
 * category. Cards are NavLinks for available tools; planned cards
 * are rendered greyed-out so the user sees the future surface
 * area but understands they can't enter yet (and don't get
 * silently dropped at a 404).
 *
 * Unknown categories (typo / stale bookmark) bounce back to the
 * toolbox overview so the user has somewhere to land instead of a
 * blank page.
 */
export default function CategoryView() {
  const { category } = useParams<{ category?: string }>();
  const cat = getCategory(category);

  if (!cat) {
    return <Navigate to="/tools" replace />;
  }

  const list = getToolsByCategory(cat.id);
  const Icon = cat.icon;

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <header className="flex items-end justify-between gap-3">
        <div>
          <Link
            to="/tools"
            className="btn-ghost mb-1 px-1 text-xs"
            title="返回工具广场"
          >
            <ArrowLeft className="h-3 w-3" />
            工具广场
          </Link>
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-accent" />
            <h1 className="text-lg font-semibold tracking-tight text-ink">{cat.label}</h1>
            <span className="badge">{list.length} 项</span>
          </div>
          <p className="mt-1 text-[12px] text-ink-muted">{cat.description}</p>
        </div>
      </header>

      {list.length === 0 ? (
        <div className="card-pad text-center text-sm text-ink-muted">
          这个分类暂时没有工具。
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((tool) => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2 text-[11px] text-ink-dim">
        <span>切到其他分类：</span>
        {categories
          .filter((c) => c.id !== cat.id)
          .map((c) => (
            <Link
              key={c.id}
              to={`/tools/${c.id}`}
              className="rounded border border-line px-1.5 py-0.5 text-ink-muted hover:bg-bg-soft hover:text-ink"
            >
              {c.shortLabel}
            </Link>
          ))}
      </div>
    </div>
  );
}

function ToolCard({ tool }: { tool: ToolDef }): JSX.Element {
  const Icon = tool.icon;
  const isAvailable = tool.status === 'available';

  const body = (
    <div
      className={clsx(
        'card-pad flex h-full flex-col gap-1.5 transition',
        isAvailable
          ? 'group hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md'
          : 'cursor-default opacity-60',
      )}
    >
      <div className="flex items-center gap-2">
        <Icon
          className={clsx('h-4 w-4', isAvailable ? 'text-accent' : 'text-ink-muted')}
        />
        <span className="font-medium text-ink">{tool.name}</span>
        {!isAvailable && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-dim">
            <Lock className="h-3 w-3" /> 规划中
          </span>
        )}
        {isAvailable && (
          <ArrowRight className="ml-auto h-4 w-4 text-ink-dim transition group-hover:translate-x-0.5 group-hover:text-accent" />
        )}
      </div>
      <p className="text-[12px] leading-snug text-ink-muted">{tool.description}</p>
      <div className="mt-auto flex items-center gap-2 text-[10px] text-ink-dim">
        <span className="font-mono">{tool.path}</span>
        <span className="ml-auto rounded bg-bg-soft px-1.5 py-0.5 font-mono">{tool.priority}</span>
      </div>
    </div>
  );

  if (isAvailable) {
    return (
      <Link to={tool.path} className="block">
        {body}
      </Link>
    );
  }
  // Planned card stays as a NavLink so the URL is still
  // shareable; ToolPlaceholder handles the fallback render.
  return (
    <Link to={tool.path} className="block">
      {body}
    </Link>
  );
}
